import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import '../../college-theme.css';
import { DeskLink } from './essayShared';

const ROLE_TAGS = { PARENT: 'Parent', ADVISOR: 'Advisor' };

// The frame around anything a parent or advisor is looking at: whose desk this
// is, said plainly at the top of every screen, then the Desk's own header. The
// tab row is optional — the desk overview has one, a single essay doesn't.
export default function SharedDeskChrome({ grant, viewerName, tabs, children }) {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const todayShort = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  // Guests never reach the School app's settings page, so signing out lives
  // here — it is the only chrome they ever see.
  const otherDesks = (user?.sharedDesks || []).filter((d) => d.grantId !== grant.id);

  return (
    <div className="college-scope" style={{ minHeight: '100vh' }}>
      <div
        style={{
          background: 'var(--college-red)',
          color: 'var(--college-bg)',
          padding: '9px 32px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <span style={{ fontSize: 12, fontWeight: 600 }}>
          You're viewing {grant.ownerName}'s college applications
        </span>
        <span
          style={{
            padding: '2px 9px',
            background: 'rgba(245,240,230,0.18)',
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
          }}
        >
          {ROLE_TAGS[grant.role]} · view {grant.comment ? '& comment' : 'only'}
        </span>
        <span style={{ marginLeft: 'auto', fontSize: 11.5, color: 'rgba(245,240,230,0.75)' }}>
          {grant.ownerName} controls what you see and can change it at any time
        </span>
      </div>

      <div style={{ borderBottom: '1px solid var(--college-ink)' }}>
        <div
          style={{
            maxWidth: 1200,
            margin: '0 auto',
            padding: '14px 32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
          }}
        >
          <span style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--college-muted)' }}>
            {viewerName}
          </span>
          <span className="font-serif-editorial" style={{ fontSize: 22, letterSpacing: '0.02em' }}>
            The College Desk
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            {otherDesks.length > 0 && (
              <DeskLink
                style={{ fontSize: 11.5, color: 'var(--college-muted)' }}
                onClick={() => navigate('/shared')}
              >
                Switch desk
              </DeskLink>
            )}
            <DeskLink
              style={{ fontSize: 11.5, color: 'var(--college-muted)' }}
              onClick={() => {
                logout();
                navigate('/auth', { replace: true });
              }}
            >
              Sign out
            </DeskLink>
            <span className="font-mono-editorial" style={{ fontSize: 11, color: 'var(--college-muted)' }}>
              {todayShort}
            </span>
          </span>
        </div>
      </div>

      {tabs && (
        <div style={{ borderBottom: '1px solid var(--college-border)' }}>
          <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 32px', display: 'flex', gap: 28, overflowX: 'auto' }}>
            {tabs.items.map((label) => {
              const active = tabs.active === label;
              return (
                <button
                  key={label}
                  type="button"
                  onClick={() => tabs.onChange(label)}
                  style={{
                    padding: '12px 2px',
                    border: 'none',
                    background: 'none',
                    fontFamily: 'inherit',
                    fontSize: 13,
                    fontWeight: active ? 600 : 500,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    color: active ? 'var(--college-ink)' : 'var(--college-muted)',
                    borderBottom: active ? '2px solid var(--college-ink)' : '2px solid transparent',
                  }}
                >
                  {label}
                  {tabs.badges?.[label] > 0 && (
                    <span
                      className="font-mono-editorial"
                      style={{
                        fontSize: 10,
                        padding: '1px 5px',
                        background: 'var(--college-red)',
                        color: 'var(--college-bg)',
                        marginLeft: 4,
                      }}
                    >
                      {tabs.badges[label]}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {children}
    </div>
  );
}
